import { Component, Input, ChangeDetectionStrategy, OnChanges, SimpleChanges } from '@angular/core';

interface FlatNode {
    key: string;
    value: any;
    depth: number;
    isLeaf: boolean;
    isArrayItem: boolean;
    formattedValue: string;
    path: string;
    expanded: boolean;
    hasChildren: boolean;
}

@Component({
    selector: 'json-tree-view',
    template: `
    <cdk-virtual-scroll-viewport 
      itemSize="24" 
      style="height: 100%; width: 100%;">
      
      <div *cdkVirtualFor="let node of flatNodes; let i = index"
           class="tree-node"
           [class.is-leaf]="node.isLeaf"
           [style.padding-left.px]="node.depth * 16 + 8">
        
        <!-- Toggle Icon -->
        <span class="toggle-icon" 
              (click)="toggleNode(i)" 
              [class.expanded]="node.expanded"
              [class.hidden]="node.isLeaf">
          {{ getIcon(node) }}
        </span>
        
        <!-- Key -->
        <span class="node-key" [class.array-item]="node.isArrayItem">
          {{ node.key }}
        </span>
        
        <!-- Separator -->
        <span class="node-separator">: </span>
        
        <!-- Value -->
        <span class="node-value" [ngClass]="getValueClass(node)">
          {{ node.formattedValue }}
        </span>
      </div>
    </cdk-virtual-scroll-viewport>
  `,
    styles: [`
    :host {
      display: block;
      height: 100%;
      overflow: hidden;
    }
    
    .tree-node {
      height: 24px;
      display: flex;
      align-items: center;
      padding-right: 8px;
      border-bottom: 1px solid #f0f0f0;
    }
    
    .tree-node:hover {
      background-color: #fafafa;
    }
    
    .toggle-icon {
      cursor: pointer;
      width: 30px;
      display: inline-flex;
      justify-content: center;
      margin-right: 4px;
      font-size: 12px;
      padding: 2px 0px 6px;
    }
    
    .toggle-icon.hidden {
      visibility: hidden;
    }
    
    .toggle-icon:hover {
      background-color: #e0e0e0;
      border-radius: 4px;
    }
    
    .node-key {
      font-weight: bold;
      color: #0066cc;
      margin-right: 4px;
    }
    
    .node-key.array-item {
      color: #9c27b0;
    }
    
    .node-separator {
      color: #999;
    }
    
    .node-value {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    /* Value type colors */
    .value-string { color: #c7254e; }
    .value-number { color: #1a7f37; }
    .value-boolean { color: #0550ae; }
    .value-null { color: #818b98; font-style: italic; }
    .value-object { color: #666; }
    .value-array { color: #666; }
  `],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class JSONTreeViewComponent implements OnChanges {

    @Input() key: string = '';
    @Input() value: any;

    flatNodes: FlatNode[] = [];
    private expandedMap: Map<string, boolean> = new Map();

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['value'] && this.value !== undefined) {
            this.buildFlatTree();
        }
    }

    /**
     * Constrói a árvore plana a partir dos dados
     */
    private buildFlatTree(): void {
        this.flatNodes = [];
        this.flattenRecursive(this.value, this.key || 'root', 0, []);
    }

    /**
     * Função recursiva para achatar a árvore
     */
    private flattenRecursive(
        value: any,
        key: string,
        depth: number,
        parentPath: string[]
    ): void {
        const path = [...parentPath, key];
        const pathKey = path.join('.');

        const isLeaf = typeof value !== 'object' || value === null;
        const isArray = Array.isArray(value);
        const hasChildren = !isLeaf && value !== null && Object.keys(value).length > 0;

        // Verificar se está expandido (padrão: sim)
        if (!this.expandedMap.has(pathKey)) {
            this.expandedMap.set(pathKey, true);
        }

        const isExpanded = this.expandedMap.get(pathKey)!;

        // Criar o nó atual
        this.flatNodes.push({
            key: key,
            value: value,
            depth: depth,
            isLeaf: isLeaf,
            isArrayItem: key.startsWith('['),
            formattedValue: this.formatValue(value),
            path: pathKey,
            expanded: isExpanded,
            hasChildren: hasChildren
        });

        // Se for um objeto/array e estiver expandido, adicionar os filhos
        if (hasChildren && isExpanded) {
            const entries = isArray
                ? value.map((v: any, i: number) => [`[${i}]`, v] as [string, any])
                : Object.entries(value);

            for (const [childKey, childValue] of entries) {
                this.flattenRecursive(childValue, childKey, depth + 1, path);
            }
        }
    }

    /**
     * Alterna o estado expandido/colapsado de um nó
     */
    toggleNode(index: number): void {
        const node = this.flatNodes[index];

        if (node.isLeaf || !node.hasChildren) return;

        const currentState = this.expandedMap.get(node.path) ?? true;
        this.expandedMap.set(node.path, !currentState);

        // Reconstrói a árvore
        this.buildFlatTree();
    }

    /**
     * Formata o valor para exibição
     */
    private formatValue(value: any): string {
        if (value === null) return 'null';
        if (value === undefined) return 'undefined';

        if (typeof value === 'string') return `"${value}"`;
        if (typeof value === 'boolean') return value ? 'true' : 'false';
        if (typeof value === 'number') return String(value);

        if (Array.isArray(value)) {
            return `Array(${value.length})`;
        }

        if (typeof value === 'object') {
            const keys = Object.keys(value);
            return `Object{${keys.length}}`;
        }

        return String(value);
    }

    /**
     * Retorna o ícone baseado no estado do nó
     */
    getIcon(node: FlatNode): string {
        if (node.isLeaf) return '📄';
        return node.expanded ? '📂' : '📁';
    }

    /**
     * Retorna a classe CSS baseada no tipo do valor
     */
    getValueClass(node: FlatNode): string {
        if (node.isLeaf) {
            if (node.value === null) return 'value-null';
            if (typeof node.value === 'string') return 'value-string';
            if (typeof node.value === 'number') return 'value-number';
            if (typeof node.value === 'boolean') return 'value-boolean';
        }

        if (node.hasChildren) {
            return Array.isArray(node.value) ? 'value-array' : 'value-object';
        }

        return '';
    }
}